import React, { useState } from 'react';
import './ProductGrid.css';
import { useShop } from '../context/ShopContext';
import { formatPrice } from '../data/products';

const colorPalette = {
  black: '#000000',
  white: '#ffffff',
  grey: '#808080',
  gray: '#808080',
  charcoal: '#4a4a4a',
  silver: '#c0c0c0',
  blue: '#1f6feb',
  navy: '#001f3f',
  teal: '#008080',
  red: '#d81b60',
  burgundy: '#6d133f',
  pink: '#d988aa',
  purple: '#512a6f',
  plum: '#5b1c5d',
  green: '#2f9e44',
  grape: '#71368a',
};

const resolveColor = (name = '') => {
  const key = name.trim().toLowerCase();
  return colorPalette[key] || '#d9d9d9';
};

function ProductGrid({ products = [] }) {
  const { addToCart, toggleWishlist, isInWishlist } = useShop();
  const [selections, setSelections] = useState({});
  const [lightboxProduct, setLightboxProduct] = useState(null);
  const [expandedColorProduct, setExpandedColorProduct] = useState(null);

  const selectValue = (productId, key, value) => {
    setSelections((prev) => ({
      ...prev,
      [productId]: { ...(prev[productId] || {}), [key]: value },
    }));
  };

  const getSelection = (product) => {
    const saved = selections[product.id] || {};
    const size = saved.size || product.sizeOptions?.[0] || 'One Size';
    const color = saved.color || product.colorOptions?.[0] || 'Default';
    return { size, color };
  };

  const handleAddToCart = (product) => {
    const { size, color } = getSelection(product);
    addToCart(product.id, { size, color });
  };

  return (
    <>
      <div className="product-grid">
        {products.map((product) => {
          const wished = isInWishlist(product.id);
          const { size, color } = getSelection(product);

          return (
            <div className="product-card" key={product.id}>
              <div
                className="product-image clickable"
                onClick={() => setLightboxProduct(product)}
              >
                <img src={product.image} alt={product.title} />
              </div>
              <div className="product-info">
                <p className="product-category">
                  {product.categoryLabel || product.category}
                </p>
                <h3 className="product-title">{product.title}</h3>
                {product.brand && (
                  <p className="product-brand">{product.brand}</p>
                )}
                <p className="product-price">
                  {formatPrice(product.price, product.currency)}
                </p>

                <div className="product-choices">
                  <label>
                    Size
                    <select
                      value={size}
                      onChange={(e) =>
                        selectValue(product.id, 'size', e.target.value)
                      }
                    >
                      {(product.sizeOptions || ['One Size']).map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </label>
                  {product.colorOptions && product.colorOptions.length > 0 && (
                    <div
                      className="color-picker"
                      onMouseLeave={() => setExpandedColorProduct(null)}
                    >
                      <p className="color-label">
                        {product.colorOptions.length} COLORS AVAILABLE
                      </p>
                      <div className="color-swatches">
                        {(expandedColorProduct === product.id
                          ? product.colorOptions
                          : product.colorOptions.slice(0, 5)
                        ).map((opt) => (
                          <button
                            key={opt}
                            className={`swatch ${
                              color === opt ? 'active' : ''
                            }`}
                            style={{ backgroundColor: resolveColor(opt) }}
                            aria-label={opt}
                            onClick={() =>
                              selectValue(product.id, 'color', opt)
                            }
                          />
                        ))}
                        {product.colorOptions.length > 5 &&
                          expandedColorProduct !== product.id && (
                            <button
                              className="swatch more"
                              onClick={() => setExpandedColorProduct(product.id)}
                            >
                              +{product.colorOptions.length - 5}
                            </button>
                          )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="product-actions">
                  <button
                    className="primary-btn"
                    onClick={() => handleAddToCart(product)}
                  >
                    Add to Cart
                  </button>
                  <button
                    className={`ghost-btn ${wished ? 'active' : ''}`}
                    onClick={() => toggleWishlist(product.id)}
                  >
                    {wished ? '♥ Saved' : '♡ Wishlist'}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {lightboxProduct && (
        <div
          className="lightbox-backdrop"
          onClick={() => setLightboxProduct(null)}
        >
          <div
            className="lightbox-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="lightbox-close"
              onClick={() => setLightboxProduct(null)}
            >
              ✕
            </button>
            <img
              src={lightboxProduct.image}
              alt={lightboxProduct.title}
              className="lightbox-image"
            />
            <p className="lightbox-title">{lightboxProduct.title}</p>
          </div>
        </div>
      )}
    </>
  );
}

export default ProductGrid;
